import React from "react";
import { getRlOptimizationPreview, getRlOptimizationData } from "../services/rlOptimizationService";

// Drives the RL redesign panel: a fast readiness preview (no training) plus
// an on-demand goal-driven run. Kept separate from useOptimizationData.js —
// the RL response shape (as_is/to_be field names, heuristic rows, job
// registry) doesn't match the deterministic /cms/optimize pipeline's, so
// sharing state between them would mean constantly reconciling two shapes.
export default function useRlOptimizationData() {
  const [preview, setPreview] = React.useState(null);
  const [previewLoading, setPreviewLoading] = React.useState(false);
  const [previewError, setPreviewError] = React.useState("");

  const [result, setResult] = React.useState(null);
  const [running, setRunning] = React.useState(false);
  const [runError, setRunError] = React.useState("");
  // The run's identity, kept for the Save tab: `jobId` is what
  // /save-optimized-version takes as ?rl_job_id to persist THIS training run
  // rather than starting a fresh one, and `includeSubprocessRedesigns` has to
  // travel with it — if the reference has expired and the engine re-runs, a
  // mismatched flag yields a different redesign for the same job.
  const [runRef, setRunRef] = React.useState(null);

  const seqRef = React.useRef(0);

  const loadPreview = React.useCallback(async (processId) => {
    if (!processId) return;
    const seq = ++seqRef.current;
    setPreviewLoading(true);
    setPreviewError("");
    setPreview(null);
    setResult(null);
    setRunRef(null);
    setRunError("");
    try {
      const data = await getRlOptimizationPreview(processId);
      if (seq !== seqRef.current) return;
      setPreview(data);
    } catch (e) {
      if (seq !== seqRef.current) return;
      setPreviewError(e?.message || "Failed to check RL readiness");
    } finally {
      if (seq === seqRef.current) setPreviewLoading(false);
    }
  }, []);

  // `options.includeSubprocessRedesigns` is the panel's opt-in toggle — off by
  // default, since each sub-process costs an extra training run.
  const run = React.useCallback(async (processId, goal, options = {}) => {
    if (!processId) return;
    const seq = ++seqRef.current;
    setRunning(true);
    setRunError("");
    setResult(null);
    setRunRef(null);
    const includeSubprocessRedesigns = !!options.includeSubprocessRedesigns;
    try {
      // seed fixed for reproducibility; top_k=3 so trade-off cards always render.
      const { result: data, job_id } = await getRlOptimizationData(processId, {
        goal,
        seed: 42,
        top_k: 3,
        includeSubprocessRedesigns,
      });
      if (seq !== seqRef.current) return;
      setResult(data);
      setRunRef({ processId, jobId: job_id, goal, includeSubprocessRedesigns });
    } catch (e) {
      if (seq !== seqRef.current) return;
      setRunError(e?.message || "RL optimization failed");
    } finally {
      if (seq === seqRef.current) setRunning(false);
    }
  }, []);

  const reset = React.useCallback(() => {
    seqRef.current += 1;
    setPreview(null);
    setPreviewLoading(false);
    setPreviewError("");
    setResult(null);
    setRunRef(null);
    setRunning(false);
    setRunError("");
  }, []);

  return {
    preview,
    previewLoading,
    previewError,
    loadPreview,
    result,
    running,
    runError,
    run,
    runRef,
    reset,
  };
}
